import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Building, Check, X, Mail, Phone, MapPin, Calendar, DollarSign, Package, MessageSquare, Clock, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuoteViewPage() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [responded, setResponded] = useState(false);
  const [responseType, setResponseType] = useState(null);

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotes/view/${quoteId}`);
      setQuote(response.data);
      
      if (response.data.estado === 'aceptada') {
        setResponded(true);
        setResponseType('accepted');
      } else if (response.data.estado === 'rechazada_cliente') {
        setResponded(true);
        setResponseType('rejected');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 404) {
        setQuote(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/quotes/${quoteId}/accept`);
      toast.success('¡Cotización aceptada!');
      setResponded(true);
      setResponseType('accepted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/quotes/${quoteId}/reject?motivo=${encodeURIComponent(rejectReason)}`);
      toast.success('Cotización rechazada');
      setResponded(true);
      setResponseType('rejected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setProcessing(false);
    }
  };

  const getPlanDetails = (planName) => {
    const plans = {
      'Básico': { color: 'bg-blue-500', features: ['Hasta 5 propiedades', 'Calendario unificado', 'Gestión de reservas', 'Reportes básicos'] },
      'Profesional': { color: 'bg-purple-500', features: ['Hasta 15 propiedades', 'Integraciones OTA', 'Reportes avanzados', 'Soporte prioritario'] },
      'Empresarial': { color: 'bg-amber-500', features: ['Propiedades ilimitadas', 'Multi-usuario', 'Soporte 24/7', 'Gerente dedicado'] }
    };
    return plans[planName] || plans['Básico'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700 text-center">
          <CardContent className="pt-8">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Cotización no disponible</h2>
            <p className="text-slate-400 mb-6">
              Esta cotización no existe o aún no ha sido enviada.
            </p>
            <Link to="/">
              <Button>Volver al inicio</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already responded
  if (responded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-slate-700 text-center">
          <CardContent className="pt-8">
            {responseType === 'accepted' ? (
              <>
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-10 w-10 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">¡Cotización Aceptada!</h2>
                <p className="text-slate-400 mb-6">
                  Gracias por aceptar nuestra propuesta. Nuestro equipo se pondrá en contacto contigo 
                  muy pronto para completar el proceso de registro y activar tu cuenta.
                </p>
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20 mb-6">
                  <p className="text-green-400 text-sm">
                    <strong>Plan seleccionado:</strong> {quote.plan_recomendado}<br />
                    <strong>Precio mensual:</strong> ${quote.precio_mensual} USD
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-slate-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="h-10 w-10 text-slate-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Cotización Rechazada</h2>
                <p className="text-slate-400 mb-6">
                  Lamentamos que nuestra propuesta no se ajuste a tus necesidades. 
                  Si cambias de opinión o tienes alguna pregunta, no dudes en contactarnos.
                </p>
              </>
            )}
            <Link to="/">
              <Button variant="outline" className="border-slate-600 text-slate-300">
                Volver al inicio
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const planDetails = getPlanDetails(quote.plan_recomendado);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="https://static.prod-images.emergentagent.com/jobs/66419162-0afe-4996-ab1a-5d519d29062e/images/1419e719ba240b6e00d42093e097a0701e8eac9d9c7b54db9859b4af59d92017.png" alt="Edén" className="h-10 w-10 rounded" />
            <span className="text-2xl font-bold text-white" style={{fontFamily: 'Manrope'}}>
              Edén
            </span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>
            Tu Cotización Personalizada
          </h1>
          <p className="text-slate-400">
            Revisa nuestra propuesta y decide si deseas continuar
          </p>
        </div>

        {/* Quote Details */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white" style={{fontFamily: 'Manrope'}}>
                  Hola, {quote.nombre}
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Gracias por tu interés en Edén
                </CardDescription>
              </div>
              <Badge className="bg-primary">Propuesta Lista</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Your Request Summary */}
            <div className="p-4 bg-slate-900/50 rounded-lg">
              <h3 className="text-sm font-semibold text-slate-400 mb-3">Tu solicitud:</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                  <Building className="h-4 w-4 text-primary" />
                  {quote.tipo_propiedad}
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <MapPin className="h-4 w-4 text-primary" />
                  {quote.ciudad}
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Package className="h-4 w-4 text-primary" />
                  {quote.num_propiedades} propiedad(es)
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Calendar className="h-4 w-4 text-primary" />
                  {quote.num_habitaciones_aprox} habitaciones
                </div>
              </div>
            </div>

            {/* Admin Message */}
            {quote.mensaje_admin && (
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Mensaje del equipo:
                </h3>
                <p className="text-slate-300 text-sm">{quote.mensaje_admin}</p>
              </div>
            )}

            {/* Recommended Plan */}
            <div className={`p-6 rounded-xl ${planDetails.color} bg-opacity-10 border border-opacity-20`} 
                 style={{borderColor: planDetails.color.replace('bg-', '')}}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Badge className={planDetails.color + ' text-white'}>
                    Plan Recomendado
                  </Badge>
                  <h3 className="text-2xl font-bold text-white mt-2" style={{fontFamily: 'Manrope'}}>
                    {quote.plan_recomendado}
                  </h3>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">${quote.precio_mensual}</p>
                  <p className="text-slate-400 text-sm">USD / mes</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 mt-4">
                {planDetails.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="h-4 w-4 text-green-500" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Quote Date */}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Clock className="h-4 w-4" />
              Cotización enviada el {new Date(quote.fecha_envio).toLocaleDateString('es-CO', {
                year: 'numeric', month: 'long', day: 'numeric'
              })}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {!showRejectForm ? (
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              size="lg"
              className="border-slate-600 text-slate-300 hover:bg-red-500/10 hover:border-red-500 hover:text-red-400"
              onClick={() => setShowRejectForm(true)}
            >
              <X className="h-5 w-5 mr-2" />
              Rechazar
            </Button>
            <Button
              size="lg"
              className="bg-slate-800 hover:bg-slate-700"
              onClick={handleAccept}
              disabled={processing}
              data-testid="accept-quote-btn"
            >
              <Check className="h-5 w-5 mr-2" />
              {processing ? 'Procesando...' : 'Aceptar Propuesta'}
            </Button>
          </div>
        ) : (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-200">¿Por qué rechazas la propuesta? (opcional)</Label>
                <Textarea
                  placeholder="Cuéntanos para poder mejorar..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="bg-slate-900 border-slate-600 text-white"
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300"
                  onClick={() => setShowRejectForm(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleReject}
                  disabled={processing}
                >
                  {processing ? 'Procesando...' : 'Confirmar Rechazo'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <p className="text-center text-slate-500 text-sm mt-6">
          ¿Tienes preguntas? Contáctanos en{' '}
          <a href="mailto:info@connectionadminhotel.com" className="text-primary hover:underline">
            info@connectionadminhotel.com
          </a>
        </p>
      </div>
    </div>
  );
}
